import React, { useState, useEffect, useRef } from 'react';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import PageHeader from '../../components/PageHeader/PageHeader';
import './Contact.css';

// Reusable Scroll Reveal Component
const RevealOnScroll = ({ children, className = '', delay = 0 }) => {
  const [isVisible, setIsVisible] = useState(false);
  const domRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (let i = 0; i < entries.length; i++) {
          setIsVisible(entries[i].isIntersecting);
        }
      },
      { threshold: 0.1 }
    );
    const current = domRef.current;
    if (current) observer.observe(current);
    return () => { if (current) observer.unobserve(current); };
  }, []);

  return (
    <div ref={domRef} className={`reveal-wrapper ${isVisible ? 'is-visible' : ''} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
};

export default function Contact() {
  const linkStyle = {
    color: 'inherit',
    textDecoration: 'none',
    transition: 'color 0.3s ease'
  };

  return (
    <div className="contact-page">
      <PageHeader
        title={<>Get In <br /><span>Touch</span></>}
        description="We are ready to assist you with your physical security, fibre infrastructure, solar setups, and software development needs."
      />

      <div className="contact-main-content">
        <div className="container contact-content">

          <RevealOnScroll className="contact-details">
            <span className="section-label">CONTACT US</span>
            <h2 className="section-title" style={{ marginBottom: '10px' }}>We're Here to Help</h2>
            <p style={{ color: 'var(--text-gray)', marginBottom: '40px', fontSize: '18px', lineHeight: '1.6' }}>
              Give us a call or drop by our office. We are happy to answer your questions and discuss your enterprise needs within 24 hours on business days.
            </p>

            <div className="contact-info-block">
              <h3>Pretoria Office</h3>
              <div className="info-row">
                <MapPin className="info-icon" size={24} />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=1177+Park+Street,+211+The+Lofts,+Hatfield,+0083"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                  onMouseOver={(e) => e.target.style.color = 'var(--brand-green)'}
                  onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                  1177 Park Street, 211 The Lofts, Hatfield, 0083
                </a>
              </div>
              <div className="info-row">
                <Phone className="info-icon" size={24} />
                <a
                  href="tel:+27158802125"
                  style={linkStyle}
                  onMouseOver={(e) => e.target.style.color = 'var(--brand-green)'}
                  onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                  +27 (015) 880 2125
                </a>
              </div>
              <div className="info-row">
                <Mail className="info-icon" size={24} />
                <a
                  href="mailto:support@singularitytech.co.za"
                  style={linkStyle}
                  onMouseOver={(e) => e.target.style.color = 'var(--brand-green)'}
                  onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                  support@singularitytech.co.za
                </a>
                <span> /</span>
                <a
                  href="mailto:info@singularitytech.co.za"
                  style={linkStyle}
                  onMouseOver={(e) => e.target.style.color = 'var(--brand-green)'}
                  onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                  info@singularitytech.co.za
                </a>
              </div>
              <div className="info-row">
                <Clock className="info-icon" size={24} />
                <span>Mon - Fri: 08:00 AM - 16:00 PM</span>
              </div>
            </div>

            <div className="contact-info-block">
              <h3>Polokwane Office</h3>
              <div className="info-row">
                <MapPin className="info-icon" size={24} />
                <a
                  href="https://www.google.com/maps/search/?api=1&query=11+Pierre+Street,+IT+Park,+Bendor+Park,+Polokwane+0699"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={linkStyle}
                  onMouseOver={(e) => e.target.style.color = 'var(--brand-green)'}
                  onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                  11 Pierre Street, IT Park, Bendor Park, Polokwane 0699
                </a>
              </div>
              <div className="info-row">
                <Phone className="info-icon" size={24} />
                <a
                  href="tel:+27663769466"
                  style={linkStyle}
                  onMouseOver={(e) => e.target.style.color = 'var(--brand-green)'}
                  onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                  +27 66 376 9466
                </a>
              </div>
              <div className="info-row">
                <Mail className="info-icon" size={24} />
                <a
                  href="mailto:support@singularitytech.co.za"
                  style={linkStyle}
                  onMouseOver={(e) => e.target.style.color = 'var(--brand-green)'}
                  onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                  support@singularitytech.co.za
                </a>

                <span> /</span>
                <a
                  href="mailto:info@singularitytech.co.za"
                  style={linkStyle}
                  onMouseOver={(e) => e.target.style.color = 'var(--brand-green)'}
                  onMouseOut={(e) => e.target.style.color = 'inherit'}
                >
                  info@singularitytech.co.za
                </a>
              </div>
              <div className="info-row">
                <Clock className="info-icon" size={24} />
                <span>Mon - Fri: 08:00 AM - 16:00 PM</span>
              </div>
            </div>
          </RevealOnScroll>

          <RevealOnScroll className="contact-map-section" delay={0.2}>
            <div className="map-wrapper">
              <iframe
                title="SingularityTech Office Location Map"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3655.434033285496!2d29.47953251543034!3d-23.87515488452815!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1ec6d8196e858db1%3A0x6b72a0eb9473b98!2s11%20Pierre%20St%2C%20Bendor%2C%20Polokwane%2C%200699!5e0!3m2!1sen!2sza!4v1700000000000!5m2!1sen!2sza"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </RevealOnScroll>

        </div>
      </div>
    </div>
  );
}